import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '@fc-core/services/error-parsing';
import { extractTextFromOps } from '@fc-shared/utils/quill.utils';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ChatBaseMessage, ChatMessage } from '../models/chat-message';
import { BotView, BotWidgetOutput } from '../models/bot-widget';
import {
  canLoadNextPickerPage,
  hasPanelAction,
  PICKER_CANCEL_ACTION_ID,
  PICKER_NEXT_PAGE_ACTION_ID,
} from '../utils/bot-panel-format';
import {
  isBotNotice,
  isBotPanel,
  isBotRecord,
  isBotView,
} from '../utils/bot-widget-guards';
import { MessageType } from '../models/message-type';
import { ChatWsService } from './chat-ws.service';
import { ChatMessagesApiService } from './chat-messages-api.service';

/**
 * The panel above the composer. It shows either a live search tied to the
 * command being typed, or a pinned temporary message: any ephemeral bot
 * reply to a sent command or a pick, which outlives the composer text.
 * Bot replies use the originating message ID.
 */
@Injectable({ providedIn: 'root' })
export class BotSearchService {
  private ws = inject(ChatWsService);
  private api = inject(ChatMessagesApiService);
  readonly message = signal<ChatMessage | null>(null);
  readonly loading = signal(false);
  readonly isLoadingMore = signal(false);
  readonly error = signal('');
  readonly channel = signal<string | null>(null);
  readonly active = signal(false);
  readonly isPinned = signal(false);
  readonly isSearching = computed(() => this.active() && !this.isPinned());
  readonly completed = signal<{ channel: string; text: string } | null>(null);
  readonly viewPicked = new Subject<BotView>();
  readonly panel = computed(() => {
    const widget = this.message()?.widget;
    return isBotPanel(widget) ? widget : null;
  });
  readonly record = computed(() => {
    const widget = this.message()?.widget;
    return isBotRecord(widget) ? widget : null;
  });
  readonly view = computed(() => {
    const widget = this.message()?.widget;
    return isBotView(widget) ? widget : null;
  });
  readonly canLoadMore = computed(() => canLoadNextPickerPage(this.panel()));
  readonly notice = computed(() => {
    const widget = this.message()?.widget;
    return isBotNotice(widget) ? widget : null;
  });
  readonly text = computed(() =>
    extractTextFromOps(this.message()?.content.text ?? []),
  );
  private selecting = new Set<string>();
  private session: {
    id: string;
    command: string;
    employee: string;
    query: string;
    sent: string;
  } | null = null;
  private ownedIds = new Set<string>();
  private pending = false;
  private timer?: ReturnType<typeof setTimeout>;

  update(text: string, channel: string, employee: string): void {
    if (this.isPinned() && text.trimStart().startsWith('/')) this.dismiss();
    const match = text
      .trim()
      .match(/^(\/fci\s+(?:vehicle|device)|\/giphy)\s+([^\n]+)$/i);
    if (
      !match ||
      (match[1].toLowerCase() === '/giphy' && match[2] === 'help')
    ) {
      if (this.isSearching()) this.dismiss();
      return;
    }
    const command = match[1].toLowerCase().replace(/\s+/g, ' ');
    if (this.channel() !== channel || this.session?.command !== command) {
      this.dismiss();
      this.channel.set(channel);
      this.session = { id: uuidv4(), command, employee, query: '', sent: '' };
      this.ownedIds.add(this.session.id);
    }
    const query = `${command} ${match[2].trim()}`;
    if (this.session!.query === query) return;
    this.session!.query = query;
    this.active.set(true);
    this.loading.set(true);
    this.error.set('');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(), 200);
  }

  private search(): void {
    const session = this.session;
    if (!session || this.pending || session.sent === session.query) return;
    this.pending = true;
    session.sent = session.query;
    const interactionId = this.panel()?.interactionId;
    if (interactionId)
      void this.api.dismissWidget(interactionId).catch(() => {});
    this.message.set(null);
    this.ws.sendMessage({
      id: session.id,
      type: MessageType.MESSAGE,
      channel: this.channel()!,
      invoker: session.employee,
      content: {
        text: [{ insert: `${session.sent}\n` }],
        attachments: [],
        previews: [],
      },
    });
  }

  /** Called before messages enter history, including private command echoes. */
  receive(message: ChatBaseMessage): boolean {
    if (
      message.type !== MessageType.MESSAGE &&
      message.type !== MessageType.UPDATE &&
      message.type !== MessageType.DELETE
    )
      return false;
    if (message.type !== MessageType.DELETE && isCommandEcho(message))
      return true;
    const owned = this.ownedIds.has(message.id);
    if (message.type !== MessageType.DELETE) {
      const fromBot = isBotAuthored(message);
      const temporary = fromBot && message.meta?.ephemeral === true;
      if (temporary && this.channel() && message.channel !== this.channel())
        return true;
      // A chosen GIF or a Meet link is a real channel message, even though it
      // reuses the command's ID.
      if (owned && fromBot && !temporary) {
        this.selecting.delete(message.id);
        if (
          message.id === this.message()?.id ||
          message.id === this.session?.id
        )
          this.dismiss();
        return false;
      }
      if (
        temporary &&
        this.selecting.has(message.id) &&
        isBotView(message.widget) &&
        message.widget.details
      ) {
        // Dismiss while the ID is still in `selecting`, so the consumed
        // picker interaction isn't reported as dismissed to the backend.
        if (this.message()?.id === message.id) this.dismiss();
        this.selecting.delete(message.id);
        this.ownedIds.add(message.id);
        this.viewPicked.next(message.widget);
        return true;
      }
      if (temporary && (this.selecting.has(message.id) || !owned)) {
        this.selecting.delete(message.id);
        this.pin(message);
        return true;
      }
    }
    if (
      owned &&
      message.id !== this.session?.id &&
      message.id !== this.message()?.id
    )
      return true;
    if (message.type === MessageType.DELETE) {
      if (message.id !== this.message()?.id && !owned) return false;
      this.dismiss();
      return true;
    }
    if (!owned) return false;
    if (!message.meta?.ephemeral) {
      this.dismiss();
      return false;
    }
    if (this.session && this.pending) {
      this.pending = false;
      if (this.session.query !== this.session.sent) {
        this.search();
        return true;
      }
    }
    this.show(this.withPagedItems(message));
    return true;
  }

  private pin(message: ChatMessage): void {
    if (message.id !== this.message()?.id) this.dismiss();
    clearTimeout(this.timer);
    this.session = null;
    this.pending = false;
    this.isPinned.set(true);
    this.show(this.withPagedItems(message));
  }

  private show(message: ChatMessage): void {
    this.ownedIds.add(message.id);
    this.channel.set(message.channel ?? null);
    this.message.set(message);
    this.error.set('');
    this.loading.set(false);
    this.isLoadingMore.set(false);
    this.active.set(true);
  }

  private withPagedItems(message: ChatMessage): ChatMessage {
    const previous = this.message();
    const widget = message.widget;
    if (
      !isBotPanel(widget) ||
      previous?.id !== message.id ||
      !isBotPanel(previous.widget) ||
      widget.interactionId !== previous.widget.interactionId ||
      (widget.items !== undefined && !widget.itemsOffset)
    )
      return message;
    const items = [...(previous.widget.items ?? [])];
    (widget.items ?? []).forEach(
      (item, index) => (items[(widget.itemsOffset ?? 0) + index] = item),
    );
    return { ...message, widget: { ...widget, items } };
  }

  async submit(output: BotWidgetOutput): Promise<void> {
    const message = this.message();
    const interactionId = this.panel()?.interactionId;
    if (!interactionId || this.loading()) return;
    const query = this.session?.query;
    this.loading.set(true);
    this.error.set('');
    if (output['id'] != null) this.selecting.add(message!.id);
    try {
      await this.api.submitWidgetOutput(interactionId, output);
      if (
        output['id'] != null ||
        output['action'] === PICKER_CANCEL_ACTION_ID
      ) {
        if (query && message?.channel)
          this.completed.set({ channel: message.channel, text: query });
        if (this.message() === message) this.dismiss();
      }
    } catch (error) {
      this.selecting.delete(message!.id);
      this.pending = false;
      if (this.message() !== message) return;
      this.handleSubmitError(error);
    }
  }

  async loadMore(): Promise<void> {
    const message = this.message();
    const interactionId = this.panel()?.interactionId;
    if (
      !interactionId ||
      !this.canLoadMore() ||
      this.loading() ||
      this.isLoadingMore()
    )
      return;
    this.isLoadingMore.set(true);
    this.pending = true;
    this.error.set('');
    try {
      // A 204 carries no rows: the next page arrives as a websocket
      // re-broadcast of the picker, which is what clears isLoadingMore.
      await this.api.submitWidgetOutput(interactionId, {
        action: PICKER_NEXT_PAGE_ACTION_ID,
      });
    } catch (error) {
      this.pending = false;
      if (this.message() !== message) return;
      this.isLoadingMore.set(false);
      this.handleSubmitError(error);
    }
  }

  close(): void {
    const query = this.session?.query;
    const channel = this.message()?.channel;
    if (
      hasPanelAction(this.panel(), PICKER_CANCEL_ACTION_ID) &&
      query &&
      channel
    )
      this.completed.set({ channel, text: query });
    this.dismiss();
  }

  private handleSubmitError(error: unknown): void {
    if (
      error instanceof HttpErrorResponse &&
      [404, 409, 410].includes(error.status)
    ) {
      this.dismiss();
      return;
    }
    this.error.set(
      error instanceof HttpErrorResponse
        ? getErrorMessage(error)
        : 'Could not complete this action. Please try again.',
    );
    this.loading.set(false);
  }

  dismissSearch(): void {
    if (this.isSearching()) this.dismiss();
  }

  dismiss(): void {
    const interactionId = this.panel()?.interactionId;
    if (interactionId && !this.selecting.has(this.message()!.id)) {
      void this.api.dismissWidget(interactionId).catch(() => {});
    }
    clearTimeout(this.timer);
    this.session = null;
    this.pending = false;
    this.message.set(null);
    this.loading.set(false);
    this.isLoadingMore.set(false);
    this.error.set('');
    this.active.set(false);
    this.isPinned.set(false);
  }
}

function isBotAuthored(message: ChatMessage): boolean {
  return message.origin === 'bot' || message.senderType === 'bot';
}

// The backend echoes a sent slash command back to its sender; the bot's reply
// (panel or channel message) then reuses the echo's ID, so showing the echo in
// the thread only flashes the command text before the reply takes over.
function isCommandEcho(message: ChatMessage): boolean {
  return message.origin === 'employee' && message.meta?.ephemeral === true;
}
